/**
 * Runtime: Edge/Node/Browser
 */

import type Sentry from '@sentry/nextjs';

// title：事件标题，ignoreErrors 实际上是匹配这个字段的。
// 字符串匹配：使用部分匹配（contains）逻辑，即只要错误消息中包含该字符串，就会被过滤。
// 正则表达式匹配：使用正则匹配，适合需要精确匹配的场景
export const ignoredErrorMessages = [
  // ================ 第三方脚本错误，无需关心
  'HTMLIFrameElement object could not be cloned',
  'Event `Event` (type=error) captured as promise rejection',
  'Failed to connect to MetaMask',
  'Should not already be working',
  'runtime.sendMessage', // Browser Extension
  // Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone
  // Error invoking postMessage: Java object is gone
  'Java object is gone',
  // Failed to load Google Translate script
  // Failed to load OneTrust script
  /Failed to load .+ script/,

  // ================ TODO: 屏蔽。需要排查处理或修复
  /^AxiosError:.+/,
  /^NotAllowedError:.+/,
  /^InvalidParametersError".+/,
  /.+is undefined/,
  /.+is not an object/,
  /.+is not a function/,
  'N+1 API Call',
  'Network Error',
  'Script error',
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
  'Cannot read properties of',
  'Cannot assign to read only property',
  'Cannot destructure property',
  "Can't find variable: EmptyRanges",
  'The operation is',
  'Right side of assignment cannot be destructured',
  'Maximum update depth exceeded',
  'Maximum call stack size exceeded',
  'Load failed',
  'Illegal invocation',
  'Importing a module script failed',
  'Access is denied for this document',
  'WKWebView API client did not respond to this postMessage',
  'Page already has an active payment session',
  'Failed to fetch',
  'Invalid or unexpected token',
  'Unexpected identifier',
  'Connection closed',
  'Vd',
];

export const clientDenyUrls = [
  'apps.voc.ai',
  'analytics.google.com',
  'fonts.googleapis.com',
  '.gstatic.com',
  '.bytedance.com',
  '.tiktok.com',
  '.emarsys.net',
  '.applovin.com',
  '.bing.com',
  '.albss.com',
  '.clarity.ms',
  '.scarabresearch.com',
  '.shulex-voc.com',
  '.facebook.net',
  '.pepperjam.com',
  '.doubleclick.net',
  '.dwin1.com',
  '.apitd.net',
  '.creativecdn.com',
  '.trustdecision.com',
  '.criteo.net',
  '.ads-twitter.com',
  '.axon.ai',
  '.zijieapi.com',
  '.roeye.com',
  '.roeyecdn.com',
  '.awswaf.com',
];

const botUserAgentPattern =
  /bot|crawler|spider|crawling|preview|headless|lighthouse|pagespeed|slurp|bingpreview|facebookexternalhit|curl|wget/i;
const browserExtensionUrlPattern = /^(chrome-extension|moz-extension|safari-web-extension):\/\//i;

function getRequestUrl(event: Sentry.Event) {
  const requestUrl = event?.request?.url;

  return typeof requestUrl === 'string' ? requestUrl : '';
}

function getHeaderValue(headers: Record<string, unknown> | undefined, headerName: string) {
  if (!headers) {
    return '';
  }

  const matchedHeader = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === headerName.toLowerCase(),
  )?.[1];

  return typeof matchedHeader === 'string' ? matchedHeader : '';
}

function getEventUserAgent(event?: Pick<Sentry.Event, 'request'>) {
  const requestHeaders = event?.request?.headers as Record<string, unknown> | undefined;
  const headerUserAgent = getHeaderValue(requestHeaders, 'user-agent');

  if (headerUserAgent) {
    return headerUserAgent;
  }

  if (typeof navigator === 'undefined') {
    return '';
  }

  return navigator?.userAgent || '';
}

export function isBotTraffic(event?: Pick<Sentry.Event, 'request'>) {
  return botUserAgentPattern.test(getEventUserAgent(event));
}

// 页面可见性追踪：后台打开或曾进入后台的页面，性能指标不可靠，需过滤
let wasEverHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';

if (typeof document !== 'undefined' && !wasEverHidden) {
  const onVisibilityChange = () => {
    wasEverHidden = true;
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
}

export function wasPageEverHidden() {
  return wasEverHidden;
}

export function isBrowserExtensionNoise(event: Sentry.Event) {
  const requestUrl = getRequestUrl(event);

  if (browserExtensionUrlPattern.test(requestUrl)) {
    return true;
  }

  return (
    event?.exception?.values?.some((exception) => {
      const stackFrames = exception?.stacktrace?.frames ?? [];

      return stackFrames.some((frame) => {
        const filename = frame?.filename || '';

        return browserExtensionUrlPattern.test(filename);
      });
    }) ?? false
  );
}
